import React from 'react';
import classnames from 'clsx';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { noop } from 'ui-components/default-props/noop';
import { emptyArray } from 'ui-components/default-props/empty';

import { ReactTableHeaderRow } from './react-table-header-row';
import { ReactTableBodyRows } from './react-table-body-rows';
import { RowGroupHeader } from './row-group-header';

import style from './react-table-wrapper.module.less';

/**
 * Presentation wrapper around react-table v7. Consumers call `useTable` (and any
 * plugin hooks) themselves, then pass the resulting instance props directly here.
 * @param {object} props
 * @param {string} [props.className]
 * @param {string} [props.headerRowClassName]
 * @param {(row: any) => string} [props.getBodyRowClassName]
 * @param {() => import('react').HTMLAttributes<HTMLDivElement>} props.getTableProps
 * @param {import('../../../prop-types/react-table').ReactTableHeaderGroupPropType[]} [props.headerGroups]
 * @param {() => import('react').HTMLAttributes<HTMLDivElement>} props.getTableBodyProps
 * @param {import('../../../prop-types/react-table').ReactTableRowPropType[]} [props.rows]
 * @param {(row: any) => void} props.prepareRow
 * @param {React.ReactNode} [props.emptyRow]
 * @param {(row: any) => React.ReactNode} [props.renderSubRow]
 * @param {boolean} [props.isLoading]
 * @param {Array<{header?: React.ReactNode, rows: import('../../../prop-types/react-table').ReactTableRowPropType[]}>} [props.rowGroups]
 * @param {boolean} [props.memoizeRows]
 * @param {string} [props.ariaLabel]
 * @param {string} [props.cellClassName]
 * @param {(row: any) => void} [props.onRowClick]
 * @returns {React.ReactElement}
 */
export const ReactTableWrapper = ({
  className,
  headerRowClassName,
  getBodyRowClassName = noop,
  getTableProps,
  headerGroups = emptyArray,
  getTableBodyProps,
  rows = emptyArray,
  prepareRow,
  emptyRow,
  renderSubRow,
  isLoading = false,
  rowGroups = emptyArray,
  memoizeRows = false,
  ariaLabel,
  cellClassName,
  onRowClick,
}) => {
  const { getHeaderGroupProps, headers } = headerGroups[0];

  const hasRows = !!rows.length;
  const hasRowGroups = !!rowGroups.length;
  const shouldShowEmptyRow = !isLoading && !hasRows && !hasRowGroups;
  const shouldShowRowGroups = !isLoading && hasRowGroups;
  const shouldShowRows = !isLoading && hasRows;

  return (
    <div {...getTableProps()} aria-label={ariaLabel} className={classnames(style['react-table-wrapper'], className)}>
      <ReactTableHeaderRow className={headerRowClassName} getHeaderGroupProps={getHeaderGroupProps} headers={headers} />
      {isLoading && (
        <div {...getTableBodyProps()}>
          <LoadingIndicator className={style['loading-indicator']} />
        </div>
      )}
      {shouldShowEmptyRow && <div {...getTableBodyProps()}>{emptyRow}</div>}
      {shouldShowRows && (
        <div {...getTableBodyProps()}>
          <ReactTableBodyRows
            cellClassName={cellClassName}
            getBodyRowClassName={getBodyRowClassName}
            memoizeRows={memoizeRows}
            onRowClick={onRowClick}
            prepareRow={prepareRow}
            renderSubRow={renderSubRow}
            rows={rows}
          />
        </div>
      )}
      {shouldShowRowGroups &&
        rowGroups.map((rowGroup, index) => {
          const { header, rows: rowGroupRows } = rowGroup;
          return (
            <div key={index} {...getTableBodyProps()}>
              {header && <RowGroupHeader colSpan={headers.length}>{header}</RowGroupHeader>}
              <ReactTableBodyRows
                cellClassName={cellClassName}
                getBodyRowClassName={getBodyRowClassName}
                onRowClick={onRowClick}
                prepareRow={prepareRow}
                renderSubRow={renderSubRow}
                rows={rowGroupRows}
              />
            </div>
          );
        })}
    </div>
  );
};
